import { Input, Menu } from '@wemolo/components'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import styles from './WemoloSearchableDropdown.module.css'
import { WemoloMenuOption, WemoloValue } from './WemoloDropdown'

export type WemoloSearchableDropdownProps = {
    options: WemoloMenuOption[]
    value?: WemoloValue
    onChange?: (value: WemoloValue | undefined) => void
    placeholder?: string
    disabled?: boolean
    clearable?: boolean
    label?: string
    hint?: string
    error?: boolean
    'data-testid'?: string
}

export const WemoloSearchableDropdown: React.FC<WemoloSearchableDropdownProps> = ({
    options,
    value,
    onChange,
    placeholder,
    disabled,
    clearable = true,
    label,
    hint,
    error,
    'data-testid': dataTestId
}) => {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [highlightIndex, setHighlightIndex] = useState(0)
    const containerRef = useRef<HTMLDivElement>(null)

    const selectedOption = useMemo(() => options.find((o) => o.value === value), [options, value])

    const filteredOptions = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return options
        return options.filter((o) => o.label.toLowerCase().includes(q))
    }, [options, query])

    useEffect(() => {
        if (!open) return
        const onDocMouseDown = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false)
                setQuery('')
            }
        }
        document.addEventListener('mousedown', onDocMouseDown)
        return () => document.removeEventListener('mousedown', onDocMouseDown)
    }, [open])

    useEffect(() => {
        setHighlightIndex(0)
    }, [query, open])

    const commitSelection = (next: WemoloValue) => {
        onChange?.(next)
        setOpen(false)
        setQuery('')
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            if (!open) {
                setOpen(true)
                return
            }
            setHighlightIndex((i) => Math.min(filteredOptions.length - 1, i + 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            if (!open) return
            setHighlightIndex((i) => Math.max(0, i - 1))
        } else if (e.key === 'Enter') {
            if (!open) return
            e.preventDefault()
            const opt = filteredOptions[highlightIndex]
            if (opt) commitSelection(opt.value)
        } else if (e.key === 'Escape') {
            if (!open) return
            e.preventDefault()
            setOpen(false)
            setQuery('')
        } else if (e.key === 'Backspace' && clearable && !query && value !== undefined) {
            onChange?.(undefined)
        }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value)
        if (!open) setOpen(true)
    }

    const handleContainerMouseDown = () => {
        if (!disabled && !open) setOpen(true)
    }

    const displayValue = open ? query : (selectedOption?.label ?? '')
    const highlightedOption = filteredOptions[highlightIndex]
    const menuValue: WemoloValue[] = highlightedOption ? [highlightedOption.value] : []

    return (
        <div
            ref={containerRef}
            className={styles.root}
            data-testid={dataTestId}
            onKeyDown={handleKeyDown}
            onMouseDown={handleContainerMouseDown}
        >
            <Input
                value={displayValue}
                onChange={handleInputChange}
                placeholder={placeholder ?? selectedOption?.label ?? 'Search…'}
                label={label}
                hint={hint}
                error={error}
                disabled={disabled}
                autoComplete="off"
            />
            {open && filteredOptions.length > 0 && (
                <div className={styles.menuWrapper}>
                    <Menu
                        className={styles.menu}
                        options={filteredOptions}
                        value={menuValue}
                        onClick={commitSelection}
                    />
                </div>
            )}
        </div>
    )
}
